import { Badge, Select } from '@cloudflare/kumo';
import { useMemo, useState, type ComponentProps } from 'react';
import { AppTabs as Tabs } from '../ui/AppTabs';
import { EmptyState } from '../ui/EmptyState';
import { SearchField } from '../ui/SearchField';
import { ChannelBadge } from './ChannelBadge';
import { INTENT_LABEL } from './intents';
import { SkeletonList } from '../ui/Skeleton';

type Conversation = {
  id: string;
  channel: 'whatsapp' | 'instagram' | 'widget';
  status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED';
  lastMessageAt: string;
  lastPreview: string | null;
  unread: number;
  contact: { id: string; nome: string; telefone: string | null; tags: string[] } | null;
  assignee: { id: string; nome: string } | null;
  last: { content: string } | null;
  intent?: string | null;
  intentConfidence?: number | null;
  urgent?: boolean;
};

type Props = {
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  counts: { mine: number; queue: number; open: number };
  view: string;
  onViewChange: (v: string) => void;
  channel: string;
  onChannelChange: (c: string) => void;
  /** Filtro do modelo de decisão: '' | 'urgent' | intenção */
  intent: string;
  onIntentChange: (v: string) => void;
  q: string;
  onQueryChange: (q: string) => void;
  loading?: boolean;
};

const STATUS_LABEL: Record<Conversation['status'], string> = {
  OPEN: 'Aberta',
  AI: 'IA',
  HUMAN: 'Equipe',
  CLOSED: 'Encerrada',
};
const STATUS_BADGE: Record<Conversation['status'], ComponentProps<typeof Badge>['variant']> = {
  OPEN: 'blue',
  AI: 'neutral',
  HUMAN: 'green',
  CLOSED: 'neutral',
};

/** Valor sentinela das opções "todas" — o `Select` do Cloudscape não trata
 *  string vazia como uma opção selecionável. */
const ALL = '__all__';

const CHANNEL_OPTIONS = [
  { value: ALL, label: 'Todos os canais' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'widget', label: 'Widget do site' },
];

const INTENT_OPTIONS = [
  { value: ALL, label: 'Todas as intenções' },
  { value: 'urgent', label: 'Urgentes' },
  ...Object.entries(INTENT_LABEL).map(([k, v]) => ({ value: k, label: v.label })),
];

function fmtRel(iso: string) {
  const d = new Date(iso);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const TAB_DEFS = [
  { value: 'open', getLabel: (c: { open: number }) => `Abertas ${c.open}` },
  { value: 'mine', getLabel: (c: { mine: number }) => `Comigo ${c.mine}` },
  { value: 'queue', getLabel: (c: { queue: number }) => `Fila ${c.queue}` },
  { value: 'closed', getLabel: () => 'Fechadas' },
  { value: 'all', getLabel: () => 'Todas' },
] as const;

export function ConversationList({
  conversations, selectedId, onSelect, counts, view, onViewChange, channel, onChannelChange, intent, onIntentChange, q, onQueryChange, loading,
}: Props) {
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  // Tags em uso — para o filtro de tag
  const allTags = useMemo(() => {
    const s = new Set<string>();
    (conversations ?? []).forEach((c) => c.contact?.tags?.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [conversations]);

  const filtered = useMemo(() => {
    if (!tagFilter) return conversations ?? [];
    return (conversations ?? []).filter((c) => c.contact?.tags?.includes(tagFilter));
  }, [conversations, tagFilter]);

  const tagOptions = useMemo(
    () => [{ value: ALL, label: 'Todas as tags' }, ...allTags.filter(Boolean).map((t) => ({ value: t, label: t }))],
    [allTags],
  );

  // O `Tabs` do Cloudscape é controlado por id (string), não por índice.
  const activeTabId = TAB_DEFS.some((t) => t.value === view) ? view : 'open';

  return (
    <aside className="inbox-list" aria-label="Lista de conversas">
      <header className="inbox-list__header">
        <Tabs
          ariaLabel="Visões da caixa de entrada"
          activeTabId={activeTabId}
          onChange={({ detail }) => {
            const def = TAB_DEFS.find((t) => t.value === detail.activeTabId);
            if (def) onViewChange(def.value);
          }}
          tabs={TAB_DEFS.map((t) => ({
            id: t.value,
            label: <span data-testid={`inbox-view-${t.value}`}>{t.getLabel(counts)}</span>,
            content: null,
          }))}
        />

        <div className="inbox-list__filters">
          <SearchField
            ariaLabel="Buscar por nome, telefone ou mensagem"
            placeholder="Buscar…"
            value={q}
            onChange={onQueryChange}
            data-testid="inbox-search"
          />
          <div data-testid="inbox-channel"><Select value={(CHANNEL_OPTIONS.find((o) => o.value === (channel || ALL)) ?? null)?.value ?? null} items={CHANNEL_OPTIONS} onValueChange={(value: string | null) => {
              const v = value;
              onChannelChange(!v || v === ALL ? '' : v);
            }} aria-label="Filtrar por canal" /></div>
          <Select value={(INTENT_OPTIONS.find((o) => o.value === (intent || ALL)) ?? null)?.value ?? null} items={INTENT_OPTIONS} onValueChange={(value: string | null) => {
              const v = value;
              onIntentChange(!v || v === ALL ? '' : v);
            }} aria-label="Filtrar por intenção" data-testid="inbox-intent" />
          {allTags.length > 0 && (
            <Select value={(tagOptions.find((o) => o.value === (tagFilter ?? ALL)) ?? null)?.value ?? null} items={tagOptions} onValueChange={(value: string | null) => {
                const v = value;
                setTagFilter(!v || v === ALL ? null : v);
              }} aria-label="Filtrar por tag do contato" placeholder="Filtrar tag…" />
          )}
        </div>
      </header>

      <ul className="inbox-list__items" role="listbox" aria-busy={loading}>
        {filtered.length === 0 && !loading && (
          <li className="inbox-list__empty">
            <EmptyState
              title="Nenhuma conversa"
              description="Esta visão não tem conversas no momento."
              data-testid="inbox-list-empty"
            />
          </li>
        )}
        {loading && (
          <li className="inbox-list__empty"><SkeletonList rows={6} label="Carregando conversas" /></li>
        )}
        {filtered.map((c) => {
          const isActive = c.id === selectedId;
          const preview = c.lastPreview ?? c.last?.content ?? '';
          return (
            <li key={c.id}>
              <button
                type="button"
                role="option"
                aria-selected={isActive}
                data-testid={`inbox-conv-${c.id}`}
                className={`inbox-list__item ${isActive ? 'inbox-list__item--active' : ''}`}
                onClick={() => onSelect(c.id)}
              >
                <span className="inbox-list__item-row">
                  <ChannelBadge channel={c.channel} dense />
                  <span className="font-semibold inbox-list__name">{c.contact?.nome ?? 'Visitante sem nome'}</span>
                  {c.unread > 0 && (
                    <Badge variant="blue">{c.unread}<span className="sr-only"> não lidas</span></Badge>
                  )}
                </span>
                <span className="inbox-list__preview">{preview || 'Sem mensagens ainda'}</span>
                <span className="inbox-list__meta">
                  <Badge variant={STATUS_BADGE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                  {c.urgent && <Badge variant="red">Urgente</Badge>}
                  {c.intent && INTENT_LABEL[c.intent] && (
                    <span title={c.intentConfidence != null ? `Intenção pelo modelo de decisão · confiança ${Math.round(c.intentConfidence * 100)}%` : undefined}>
                      <Badge variant={INTENT_LABEL[c.intent].variant}>{INTENT_LABEL[c.intent].label}</Badge>
                    </span>
                  )}
                  {c.assignee && <span>{c.assignee.nome}</span>}
                  <span>· {fmtRel(c.lastMessageAt)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
